/**
 * Smart preloading (Feature #24) — precarga inteligente de anchors internos.
 *
 * Cuando el usuario acerca el cursor (o mantiene un touchstart) sobre un
 * enlace/botón que apunta a una sección con `id`, inyectamos
 * `<link rel="prefetch">` para los recursos visibles de esa sección (imágenes
 * con srcset). El objetivo es que el próximo scroll+reveal se sienta
 * instantáneo. Es un no-op si la sección ya está en caché o si el usuario
 * tiene Save-Data activado.
 */

const prefetched = new Set();

function shouldSkip() {
  const conn = navigator.connection;
  if (conn?.saveData) return true;
  if (conn?.effectiveType && /^2g|slow-2g$/.test(conn.effectiveType)) return true;
  return false;
}

function prefetchUrl(url) {
  if (!url || prefetched.has(url)) return;
  prefetched.add(url);
  const link = document.createElement("link");
  link.rel = "prefetch";
  link.as = "image";
  link.href = url;
  link.crossOrigin = "anonymous";
  document.head.appendChild(link);
}

function prefetchSection(id) {
  const section = document.getElementById(id.replace(/^#/, ""));
  if (!section) return;
  // Take first 3 img/picture src or srcset entries (avoid over-fetching)
  const imgs = Array.from(section.querySelectorAll("img")).slice(0, 3);
  imgs.forEach((img) => {
    const srcset = img.getAttribute("srcset");
    if (srcset) {
      const first = srcset.split(",")[0]?.trim().split(" ")[0];
      if (first) prefetchUrl(first);
    } else if (img.src) {
      prefetchUrl(img.src);
    }
  });
}

export function bootstrapSmartPreload() {
  if (typeof window === "undefined") return () => {};
  if (shouldSkip()) return () => {};

  const onEnter = (e) => {
    const el = e.target.closest?.("[data-preload], a[href^='#']");
    if (!el) return;
    const target = el.getAttribute("data-preload") || el.getAttribute("href");
    if (!target) return;
    prefetchSection(target);
  };

  document.addEventListener("pointerenter", onEnter, { capture: true, passive: true });
  document.addEventListener("touchstart", onEnter, { capture: true, passive: true });

  return () => {
    document.removeEventListener("pointerenter", onEnter, { capture: true });
    document.removeEventListener("touchstart", onEnter, { capture: true });
  };
}
